import { useCallback, useState, useEffect, ReactNode, ReactElement, ChangeEvent, useId } from 'react';
import { useField } from 'react-final-form';
import { FileInput, LoadingContainer } from '@ff-it/ui';
import { fieldSubscription, getVisibleFieldError, useIsDisabled, FormGroup } from '@ff-it/form';
import FileContainer from './FileContainer';
import useAttachmentUploader from '../useAttachmentUploader';
import type { Attachment } from '../types';
import { actionErrorOrThrow } from 'utilities';
import { RequestFailure } from '@ff-it/api';

interface AttachmentFieldProps {
  id?: string;
  name: string;
  label?: ReactNode;
  className?: string;
  required?: boolean;
  isDisabled?: boolean;
  discriminator?: string;
}

export function AttachmentField(props: AttachmentFieldProps): ReactElement {
  const { id, name, label, className, isDisabled: disabledProp, required, discriminator } = props;
  const [deleted, setDeleted] = useState<Attachment[]>([]);

  const {
    input: { value, onChange },
    meta: fieldMeta,
  } = useField<Attachment[], HTMLInputElement>(name, { subscription: fieldSubscription });
  const _id = useId();
  const fieldId = id || _id;

  const isDisabled = useIsDisabled(disabledProp);

  const { upload, isLoading } = useAttachmentUploader<FileList, RequestFailure<any>>({
    discriminator,
    onSuccess: (attachments) => {
      onChange(value ? [...value, ...attachments] : attachments);
    },
    onError: (err) => {
      actionErrorOrThrow(err);
    },
  });

  const onMarkRemove = useCallback(
    (id: number) => {
      const newValue = value.filter((v) => {
        if (v.id === id) {
          setDeleted((deleted) => [...deleted, v]);
          return false;
        }
        return true;
      });
      onChange(newValue);
    },
    [onChange, value],
  );

  useEffect(() => {
    if (!fieldMeta.dirty && deleted.length > 0) {
      setDeleted([]);
    }
  }, [fieldMeta.dirty, deleted]);

  const onFileListChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (!e.target.files) {
        return;
      }
      upload(e.target.files).catch((err) => {
        console.error('Upload error:', err);
      });
    },
    [upload],
  );

  return (
    <FormGroup
      className={className}
      label={label}
      id={isDisabled ? undefined : fieldId}
      required={required}
      meta={fieldMeta}
    >
      <LoadingContainer loading={isLoading}>
        <FileContainer files={value || []} markRemove={onMarkRemove} disabled={isDisabled} />
        <FileContainer files={deleted} />
        {!isDisabled && (
          <FileInput
            id={fieldId}
            onChange={onFileListChange}
            valid={getVisibleFieldError(fieldMeta) ? false : undefined}
          />
        )}
      </LoadingContainer>
    </FormGroup>
  );
}
